import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ChevronDown, Crosshair, Footprints, RotateCw } from "lucide-react";
import { scrollToId } from "../hooks/useLenis";

const HERO_IMG = "/photos/chaise-joueur.jpg";

const LINES = [
  { text: "ENTREZ DANS", accent: false },
  { text: "UNE AUTRE", accent: false },
  { text: "RÉALITÉ.", accent: true },
];

const METRICS = [
  { value: "12+", label: "EXPÉRIENCES" },
  { value: "6", label: "JOUEURS SIMULTANÉS" },
  { value: "5€", label: "DÈS" },
];

export const Hero = ({ onReserve }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), {
    stiffness: 120,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), {
    stiffness: 120,
    damping: 18,
  });

  const handleMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="cyber-grid relative flex min-h-screen items-center overflow-hidden pb-20 pt-28"
    >
      <div className="pointer-events-none absolute -left-40 top-1/4 h-96 w-96 rounded-full bg-cyan-500/10 blur-[120px]" />
      <div className="pointer-events-none absolute -right-40 bottom-1/4 h-96 w-96 rounded-full bg-fuchsia-600/10 blur-[120px]" />

      <div className="relative mx-auto grid w-full max-w-7xl items-center gap-14 px-5 lg:grid-cols-[1.15fr_1fr]">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            data-testid="hero-status-pill"
            className="glass-panel inline-flex items-center gap-2.5 px-4 py-2"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            <span className="font-hud text-[11px] uppercase tracking-[0.25em] text-emerald-300">
              5 KAT VR + 1 chaise 360° en ligne
            </span>
          </motion.div>

          <h1 className="mt-7 font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{
                    delay: 0.25 + i * 0.14,
                    duration: 0.9,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  className={`block ${
                    line.accent ? "text-glow-cyan text-cyan-400" : "text-slate-100"
                  }`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-400"
          >
            Courez, volez et combattez pour de vrai. Immersion Plus combine 5
            tapis omnidirectionnels KAT VR Walk Mini et une chaise gyroscopique
            360° pour une immersion qu'aucun salon ne peut égaler.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-reserver-btn"
              onClick={onReserve}
              className="group relative bg-cyan-400 px-8 py-4 font-hud text-sm font-bold uppercase tracking-[0.2em] text-void transition-all duration-300 hover:bg-cyan-300 hover:shadow-[0_0_36px_rgba(0,240,255,0.55)]"
            >
              Réserver une session
            </button>
            <button
              data-testid="hero-discover-btn"
              onClick={() => scrollToId("#experiences")}
              className="border border-line px-8 py-4 font-hud text-sm uppercase tracking-[0.2em] text-slate-300 transition-all duration-300 hover:border-cyan-400/60 hover:text-cyan-300"
            >
              Découvrir les jeux
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            data-testid="hero-metrics"
            className="mt-12 flex gap-10 border-t border-line pt-6"
          >
            {METRICS.map((m) => (
              <div key={m.label}>
                <div className="font-orbitron text-2xl font-bold text-cyan-400">
                  {m.value}
                </div>
                <div className="mt-1 font-hud text-[10px] uppercase tracking-[0.25em] text-slate-500">
                  {m.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative hidden lg:block"
          style={{ perspective: 1200 }}
        >
          <motion.div
            data-testid="hero-tilt-card"
            onMouseMove={handleMove}
            onMouseLeave={reset}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="clip-frame relative border border-cyan-500/30 bg-surface p-2 shadow-[0_0_60px_rgba(0,240,255,0.12)]"
          >
            <div className="clip-frame relative overflow-hidden">
              <img
                src={HERO_IMG}
                alt="Joueur en immersion VR au néon"
                className="h-[480px] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-transparent" />
              <div className="absolute left-4 top-4 flex items-center gap-2 bg-void/70 px-3 py-1.5 backdrop-blur-md">
                <Crosshair className="h-3.5 w-3.5 text-cyan-400" />
                <span className="font-hud text-[10px] uppercase tracking-[0.2em] text-cyan-300">
                  Latence &lt; 12ms
                </span>
              </div>
              <div
                className="absolute bottom-4 left-4 flex items-center gap-2 bg-void/70 px-3 py-1.5 backdrop-blur-md"
                style={{ transform: "translateZ(30px)" }}
              >
                <Footprints className="h-3.5 w-3.5 text-cyan-400" />
                <span className="font-hud text-[10px] uppercase tracking-[0.2em] text-slate-200">
                  5× KAT VR Walk Mini
                </span>
              </div>
              <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-void/70 px-3 py-1.5 backdrop-blur-md">
                <RotateCw className="h-3.5 w-3.5 text-fuchsia-400" />
                <span className="font-hud text-[10px] uppercase tracking-[0.2em] text-slate-200">
                  1× Chaise 360°
                </span>
              </div>
            </div>
          </motion.div>
          <div className="absolute -bottom-5 -right-5 -z-10 h-full w-full border border-fuchsia-500/25" />
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("#experiences")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-slate-500 transition-colors hover:text-cyan-400"
        aria-label="Défiler"
      >
        <ChevronDown className="h-6 w-6 animate-bounce" />
      </motion.button>
    </section>
  );
};
